import { useCallback, useEffect, useState } from "react";

import { Classes, Dialog } from "@blueprintjs/core";
import { Check, Search } from "lucide-react";

import { Pagination } from "@/components/pieces/form/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSearchCharacterHeadshotsLazyQuery } from "@/graphql";
import { cn } from "@/lib/utils";

interface CharacterHeadshotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (headshot: Headshot) => void;
  currentHeadshotId?: string;
}

export interface Headshot {
  id: string;
  age?: string | null;
  ethnicity?: string | null;
  gender?: string | null;
  attire?: string | null;
  imageUrlEasy?: string | null;
  imageUrlMedium?: string | null;
  imageUrlHard?: string | null;
  imageUrlVeryHard?: string | null;
  imageUrlDefault?: string | null;
}

interface HeadshotPhotoProps {
  headshot: Headshot;
  isSelected: boolean;
  onClick: () => void;
}

function HeadshotPhoto({ headshot, isSelected, onClick }: HeadshotPhotoProps) {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <button
      onClick={onClick}
      className={cn(
        "relative aspect-square rounded-full transition-all",
        "hover:scale-105",
        isSelected && "ring-4 ring-blue-500 ring-offset-2",
        !imageLoaded && "bg-gray-200 animate-pulse",
      )}
    >
      {headshot.imageUrlDefault && (
        <img
          src={headshot.imageUrlDefault}
          alt=""
          className={cn(
            "w-full h-full object-cover transition-opacity duration-200 rounded-full ",
            imageLoaded ? "opacity-100" : "opacity-0",
          )}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageLoaded(true)} // Hide loader even on error
        />
      )}

      {isSelected && (
        <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}
    </button>
  );
}

const generateSeed = (): number => {
  return Math.floor(Date.now() / 1000) % 2147483647;
};

function CharacterHeadshotModal({
  isOpen,
  onClose,
  onSelect,
  currentHeadshotId,
}: CharacterHeadshotModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedHeadshotId, setSelectedHeadshotId] = useState<string | null>(
    currentHeadshotId || null,
  );
  const [headshots, setHeadshots] = useState<Headshot[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [randomSeed, setRandomSeed] = useState<number | null>(null);
  const itemsPerPage = 20;

  const [searchHeadshots, { loading: searchLoading }] =
    useSearchCharacterHeadshotsLazyQuery({
      onCompleted: (data) => {
        if (data.searchCharacterHeadshots) {
          setHeadshots(data.searchCharacterHeadshots.data || []);
          setTotalCount(data.searchCharacterHeadshots.total || 0);
        } else {
          setHeadshots([]);
          setTotalCount(0);
        }
        setHasSearched(true);
      },
      onError: () => {
        setHeadshots([]);
        setTotalCount(0);
        setHasSearched(true);
      },
    });

  const performSearch = useCallback(
    (query: string, page: number) => {
      searchHeadshots({
        variables: {
          query: query || "",
          page,
          size: itemsPerPage,
          randomSeed: randomSeed,
        },
      });
    },
    [searchHeadshots, randomSeed],
  );

  const handleSearch = useCallback(() => {
    if (searchQuery.trim()) {
      setCurrentPage(1);
      setSelectedHeadshotId(null);
      performSearch(searchQuery, 1);
    }
  }, [searchQuery, performSearch]);

  // Load initial results when modal opens
  useEffect(() => {
    if (isOpen && !hasSearched) {
      const seed = generateSeed();
      setRandomSeed(seed);
      searchHeadshots({
        variables: {
          query: "",
          page: 1,
          size: itemsPerPage,
          randomSeed: seed,
        },
      });
    }
  }, [isOpen, hasSearched, searchHeadshots]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setHeadshots([]);
      setTotalCount(0);
      setHasSearched(false);
      setCurrentPage(1);
      setRandomSeed(null);
    }
  }, [isOpen]);

  const handleSave = useCallback(() => {
    if (selectedHeadshotId) {
      const selectedHeadshot = headshots.find(
        (h) => h.id === selectedHeadshotId,
      );
      if (selectedHeadshot) {
        onSelect(selectedHeadshot);
        onClose();
      }
    }
  }, [selectedHeadshotId, headshots, onSelect, onClose]);

  const renderContent = () => {
    // Loading state - show pulsing circles
    if (searchLoading) {
      return (
        <div className="grid grid-cols-4 md:grid-cols-5 gap-4">
          {Array.from({ length: 20 }).map((_, index) => (
            <div
              key={index}
              className="aspect-square rounded-full bg-gray-200 animate-pulse"
            />
          ))}
        </div>
      );
    }

    // No results state
    if (hasSearched && headshots.length === 0) {
      return (
        <div className="flex flex-col items-center text-center justify-center py-12">
          <p className="text-gray-500 mb-8">
            Try a different search, there aren't any avatars that match that
            description
          </p>
          <div className="grid grid-cols-5 gap-4 w-full opacity-30">
            {Array.from({ length: 10 }).map((_, index) => (
              <div
                key={index}
                className="aspect-square rounded-full bg-gray-100"
              />
            ))}
          </div>
        </div>
      );
    }

    // Results state
    if (headshots.length > 0) {
      return (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-4">
            {headshots.map((headshot) => (
              <HeadshotPhoto
                key={headshot.id}
                headshot={headshot}
                isSelected={selectedHeadshotId === headshot.id}
                onClick={() => setSelectedHeadshotId(headshot.id)}
              />
            ))}
          </div>
        </>
      );
    }

    // Initial state - loading
    return null;
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={<div className="text-xl p-3">Choose Photo</div>}
      className={Classes.DIALOG}
      style={{ width: "600px", maxWidth: "90vw" }}
    >
      <div className="p-6">
        <div className="space-y-6">
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
            <Input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                // Clear selection when user starts typing a new search
                if (e.target.value !== searchQuery) {
                  setSelectedHeadshotId(null);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              className="w-full pl-10 pr-28 py-2 h-12 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground"
              autoFocus={false}
            />
            <Button
              onClick={handleSearch}
              disabled={!searchQuery.trim() || searchLoading}
              size="sm"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1 h-8"
            >
              Search
            </Button>
          </div>

          <div>{renderContent()}</div>
        </div>
      </div>

      <div className="px-6 py-3 bg-white rounded-b-2xl border-t flex justify-between items-center w-full">
        <Pagination
          nbPages={Math.ceil(totalCount / itemsPerPage)}
          currentPage={currentPage}
          totalCount={totalCount}
          countPerPage={itemsPerPage}
          onPrev={() => {
            const newPage = Math.max(1, currentPage - 1);
            setCurrentPage(newPage);
            performSearch(searchQuery || "", newPage);
          }}
          onNext={() => {
            const newPage = currentPage + 1;
            setCurrentPage(newPage);
            performSearch(searchQuery || "", newPage);
          }}
        />
        <Button
          onClick={handleSave}
          disabled={!selectedHeadshotId}
          className="min-w-[100px]"
        >
          Select
        </Button>
      </div>
    </Dialog>
  );
}

export default CharacterHeadshotModal;
